import React from 'react';
import { makeStyles } from '@mui/styles';
import { CircularProgress, Typography, Alert, AlertTitle } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ConnectorInfo, DagSection, DagConfigs, DagConfig } from 'types/types';
import { ConnectorErrorListUnified } from './Setting/ConnectorErrorListUnified';
import { ConnectorDescriptionTemplate } from './description/ConnectorDescriptionTemplate';
import { ConnectorSyncPage } from './ConnectorSyncPage';
import { useDagHistory } from './hooks/useDagHistory';

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    display: 'flex',
    justifyContent: 'Center',
    alignItems: 'center',
    marginTop: '15%'
  },
  container: { height: '100%', width: '100%' },
  errorDiv: { marginLeft: theme.spacing(2) },
  manageBottom: { paddingBottom: theme.spacing(1.25) },
  heading: { padding: '8px 16px' },
  headingText: { fontSize: theme.spacing(3) },
  dagsKey: { paddingTop: theme.spacing(2) },
  initialSetupWarning: {
    margin: theme.spacing(2, 2, 0, 2)
  }
}));

type ConnectorOverviewPageProps = {
  info: ConnectorInfo;
  installId: string;
  dagConfig: DagConfig[];
  isLoading: boolean;
  dagSetting: { dagId: string; status: string } | null;
  setDagSetting: React.Dispatch<
    React.SetStateAction<{ dagId: string; status: string } | null>
  >;
};

const ConnectorOverviewPage: React.FC<ConnectorOverviewPageProps> = ({
  info,
  installId,
  dagConfig,
  isLoading,
  dagSetting,
  setDagSetting
}: ConnectorOverviewPageProps) => {
  const classes = useStyles();

  const {
    hasSuccessfulRun: initialSetupCompleted,
    isLoading: checkingInitialSetup
  } = useDagHistory({
    companyKey: info.companyKey,
    connectorIdentifier:
      info.shortName === 'computerease'
        ? `${info.shortName}_initial_setup`
        : null,
    enabled: info.shortName === 'computerease' && info.installed
  });

  const handleCustomSetting = (dagId: string) => {
    if (!Array.isArray(dagConfig) || dagConfig.length === 0) {
      return [];
    }
    const matchingDag = dagConfig.filter(
      (item: DagConfig) => item.workflowId === dagId
    )[0];
    return matchingDag ? matchingDag.customSettings : [];
  };

  const handleDagSwitch = (dagId: string) => {
    if (!Array.isArray(dagConfig) || dagConfig.length === 0) {
      return 'Disabled';
    }
    const matchingDag = dagConfig.filter(
      (item: DagConfig) => item.workflowId === dagId
    )[0];
    return matchingDag && matchingDag.enabled === 'Yes'
      ? 'Enabled'
      : 'Disabled';
  };

  const getFilteredDagsSection = () => {
    if (info.shortName !== 'computerease') {
      return info.dagsSection;
    }

    if (!info.installed) {
      return info.dagsSection;
    }

    if (!initialSetupCompleted) {
      return info.dagsSection.filter((dag) => dag.dagId === 'initial_setup');
    }

    return info.dagsSection;
  };

  const filteredDagsSection = getFilteredDagsSection();

  const dagsObject = filteredDagsSection.reduce((acc, curr) => {
    if (!curr) return acc;
    const tab = curr.tab;

    const matchingConfig = (Array.isArray(dagConfig)
      ? dagConfig.filter((item) => item.workflowId == curr.dagId)
      : []) as DagConfigs;
    if (matchingConfig.length > 0) {
      const mappedWorkflow = matchingConfig[0]['isCustom'] || curr.dagId;
      curr.mappedWorkflow = mappedWorkflow;
    }

    if (!acc[tab]) acc[tab] = [];
    acc[tab].push(curr);

    return acc;
  }, {} as { [key: string]: DagSection[] });

  return (
    <div data-testid="connector-overview-page" className={classes.container}>
      {installId && (
        <div className={classes.errorDiv}>
          <ConnectorErrorListUnified
            data-testid="connectorDataList-testid"
            errors={null}
            retryURL={''}
          />
        </div>
      )}
      {info.shortName === 'computerease' &&
        !initialSetupCompleted &&
        !checkingInitialSetup && (
          <Alert severity="warning" className={classes.initialSetupWarning}>
            <AlertTitle>Initial Setup Required</AlertTitle>
            <FormattedMessage
              id={'intermediateOauthDialog.computerease.initialSetupWarning'}
            />
          </Alert>
        )}

      {info.dagsSection.length !== 0 && (
        <>
          <ConnectorDescriptionTemplate info={info} />
          <div className={classes.heading} data-testid="head-data">
            <Typography className={classes.headingText}>
              <FormattedMessage id={'connectorDescription.dataToSync'} />
            </Typography>
            {isLoading || checkingInitialSetup ? (
              <div className={classes.circularProgress}>
                <CircularProgress size={50} />
              </div>
            ) : (
              <div className={classes.manageBottom}>
                {dagsObject &&
                  Object.keys(dagsObject).map((dagsKey) => (
                    <div key={dagsKey}>
                      <Typography className={classes.dagsKey}>
                        {dagsKey}
                      </Typography>
                      {dagsObject[dagsKey].map((dag: DagSection, ind) => (
                        <ConnectorSyncPage
                          key={ind}
                          info={info}
                          dagId={dag.dagId}
                          arrowIcon={dag.arrowIcon}
                          dagDescription={dag.description}
                          dagSwitchStatus={handleDagSwitch(dag.dagId)}
                          initialCustomSetting={handleCustomSetting(dag.dagId)}
                          mappedWorkflow={dag?.mappedWorkflow}
                          dagSetting={dagSetting}
                          setDagSetting={setDagSetting}
                        />
                      ))}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default ConnectorOverviewPage;
